import { useQueryClient } from '@tanstack/react-query';
import { Eye, MoreHorizontal, Pencil, Plus, Power, PowerOff, TicketPercent } from 'lucide-react';
import { useState } from 'react';

import {
  getCuponsDiscussaoAdminControllerListarQueryKey,
  useCuponsDiscussaoAdminControllerAtualizar,
  useCuponsDiscussaoAdminControllerListar,
} from '@/api/generated/admin-cupons-de-desconto-discussão/admin-cupons-de-desconto-discussão';
import type { CupomDiscussaoResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { CupomDiscussaoFormDialogo } from '@/features/admin/cupons/components/CupomDiscussaoFormDialogo';
import { UsosDoCupomDialogo } from '@/features/admin/cupons/components/UsosDoCupomDialogo';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'todos';

function formatarData(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—';
}

/** "Sem prazo" · "até 31/03/2026" · "01/02/2026 – 31/03/2026". */
function descreverVigencia(cupom: CupomDiscussaoResponse): string {
  if (!cupom.validoDe && !cupom.validoAte) return 'Sem prazo';
  if (cupom.validoDe && cupom.validoAte) {
    return `${formatarData(cupom.validoDe)} – ${formatarData(cupom.validoAte)}`;
  }
  return cupom.validoDe ? `a partir de ${formatarData(cupom.validoDe)}` : `até ${formatarData(cupom.validoAte)}`;
}

/**
 * Cupons de desconto — Discussão técnica (docs/cupons-desconto-discussao.md
 * do backend). Independente do cupom do Clube (`ClubeCuponsTab`): sem
 * parceiro, sem categoria, sem faixa por ranque — só código, % de desconto e
 * limite de uso. O médico usa na etapa de pagamento ao abrir uma discussão
 * (`EtapaPagamento.tsx`).
 */
export function DiscussaoCuponsTab() {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState<string>(TODOS);
  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<CupomDiscussaoResponse | undefined>();
  const [verUsosDe, setVerUsosDe] = useState<CupomDiscussaoResponse | null>(null);

  const { data, isPending, isError, refetch } = useCuponsDiscussaoAdminControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'criadoEm',
    ordem: 'desc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(situacao !== TODOS ? { ativo: situacao as 'true' | 'false' } : {}),
  });

  const atualizar = useCuponsDiscussaoAdminControllerAtualizar();

  const podeCriar = pode('admin_cupons_discussao.criar');
  const podeEditar = pode('admin_cupons_discussao.editar');

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  async function alternarAtivo(cupom: CupomDiscussaoResponse, ativo: boolean) {
    try {
      await atualizar.mutateAsync({ id: cupom.id, data: { ativo } });
      await queryClient.invalidateQueries({ queryKey: getCuponsDiscussaoAdminControllerListarQueryKey() });
      toast.success(ativo ? `"${cupom.codigo}" reativado.` : `"${cupom.codigo}" desativado.`);
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível alterar o cupom.');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <Input
            value={busca}
            onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
            placeholder="Buscar por código..."
            className="sm:max-w-xs"
          />

          <Select value={situacao} onValueChange={(v) => aoFiltrar(() => setSituacao(v))}>
            <SelectTrigger className="sm:w-40" aria-label="Filtrar por status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos os status</SelectItem>
              <SelectItem value="true">Ativos</SelectItem>
              <SelectItem value="false">Desativados</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {podeCriar && (
          <Button
            onClick={() => {
              setEmEdicao(undefined);
              setFormAberto(true);
            }}
          >
            <Plus className="w-4 h-4" />
            Novo cupom
          </Button>
        )}
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
            Cupons de desconto — Discussão técnica
          </h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={TicketPercent}
            titulo="Nenhum cupom encontrado"
            descricao={busca ? 'Tente outro termo de busca.' : 'Crie o primeiro cupom.'}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Desconto</TableHead>
                    <TableHead>Usos</TableHead>
                    <TableHead>Repete por médico</TableHead>
                    <TableHead>Vigência</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-20">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((cupom) => (
                    <TableRow key={cupom.id}>
                      <TableCell>
                        <span className="font-medium text-foreground">{cupom.codigo}</span>
                        {cupom.descricao && (
                          <p className="text-xs text-muted-foreground mt-0.5">{cupom.descricao}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {cupom.percentualDesconto}%{cupom.percentualDesconto === 100 && (
                          <Badge variant="outline" className="ml-2 text-brand border-brand/40">
                            Integral
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {cupom.usosContados}
                        {cupom.usosMaximos !== null ? ` / ${cupom.usosMaximos}` : ' (sem limite)'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {cupom.permiteReusoPorUsuario ? 'Sim' : 'Não'}
                      </TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {descreverVigencia(cupom)}
                      </TableCell>
                      <TableCell>
                        {cupom.ativo ? (
                          <Badge variant="outline" className="text-emerald-600 border-emerald-600/40">
                            Ativo
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-destructive border-destructive/40">
                            Desativado
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label={`Ações de ${cupom.codigo}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setVerUsosDe(cupom)}>
                              <Eye className="w-4 h-4" />
                              Ver usos
                            </DropdownMenuItem>

                            {podeEditar && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setEmEdicao(cupom);
                                    setFormAberto(true);
                                  }}
                                >
                                  <Pencil className="w-4 h-4" />
                                  Editar
                                </DropdownMenuItem>

                                {cupom.ativo ? (
                                  <DropdownMenuItem
                                    onClick={() => void alternarAtivo(cupom, false)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <PowerOff className="w-4 h-4" />
                                    Desativar
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => void alternarAtivo(cupom, true)}>
                                    <Power className="w-4 h-4" />
                                    Reativar
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      <CupomDiscussaoFormDialogo
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        {...(emEdicao ? { cupom: emEdicao } : {})}
      />

      <UsosDoCupomDialogo cupom={verUsosDe} aoFechar={() => setVerUsosDe(null)} />
    </div>
  );
}
